import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowRight, Award, Wrench, Clock, Phone, CheckCircle, Shield, Gauge, Cog, Truck } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import ProductCard from '../components/ProductCard';
import PayPalInstallmentBanner from '../components/PayPalInstallmentBanner';
import { getMachines, getAccessories, getHeroImages, getServices, getCompanyInfo } from '../services/productApi';
// Static UI content (heroImages/services/companyInfo): used as initial values, replaced by API data
import { companyInfo as mockCompanyInfo, services as mockServices, heroImages as mockHeroImages, features } from '../data/mock';

const Home = () => {
  const { t } = useTranslation();
  const [machines, setMachines] = useState([]);
  const [accessories, setAccessories] = useState([]);
  const [heroImages, setHeroImages] = useState(mockHeroImages);
  const [services, setServices] = useState(mockServices);
  const [companyInfo, setCompanyInfo] = useState(mockCompanyInfo);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadData = async () => {
      try {
        const [machinesData, accessoriesData, heroData, servicesData, companyData] = await Promise.all([
          getMachines(),
          getAccessories(),
          getHeroImages(),
          getServices(),
          getCompanyInfo()
        ]);
        
        setMachines(machinesData || []);
        setAccessories(accessoriesData || []);
        
        if (heroData) setHeroImages(heroData);
        if (servicesData?.length > 0) setServices(servicesData);
        if (companyData) setCompanyInfo(companyData);
      } catch (error) {
        console.error('Error loading data:', error);
        setMachines([]);
        setAccessories([]);
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, []);

  // Filter machines by category
  const miniBagger = machines.filter(m => m.category === 'Mini-Bagger');
  const radlader = machines.filter(m => m.category === 'Radlader');
  const gabelstapler = machines.filter(m => m.category === 'Gabelstapler');
  const ruettelplatten = machines.filter(m => m.category === 'Rüttelplatte');
  const stampfer = machines.filter(m => m.category === 'Stampfer');

  return (
    <div className="min-h-screen">
      {/* 1. Hero Section - Video mittig + Highlights-Kacheln */}
      <section className="relative bg-white pt-2">
        <div data-testid="hero-main-video">
          <div className="container mx-auto px-4 sm:px-6 lg:px-2 xl:max-w-[1800px]">
            
            {/* Desktop Layout: Kacheln links – Video mitte – Kacheln rechts */}
            <div className="hidden lg:block">
              <div className="grid grid-cols-[1fr_2fr_1fr] gap-5 xl:gap-6">
                
                {/* Linke Spalte: 4 Kacheln */}
                <div className="flex flex-col gap-3 h-[400px] xl:h-[450px]">
                  <Card className="flex-1 rounded-xl overflow-hidden bg-white shadow-[0_2px_8px_rgba(0,0,0,0.06)] hover:shadow-[0_4px_12px_rgba(0,0,0,0.1)] border border-gray-100 transition-all">
                    <CardContent className="h-full flex items-center gap-3 p-4">
                      <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
                        <Shield className="text-red-600" size={20} />
                      </div>
                      <div>
                        <h3 className="text-sm font-semibold text-gray-900">Geschlossene Kabine</h3>
                        <p className="text-xs text-gray-500">Standard bei allen unseren Modellen</p>
                      </div>
                    </CardContent>
                  </Card>

                  <Card className="flex-1 rounded-xl overflow-hidden bg-white shadow-[0_2px_8px_rgba(0,0,0,0.06)] hover:shadow-[0_4px_12px_rgba(0,0,0,0.1)] border border-gray-100 transition-all">
                    <CardContent className="h-full flex items-center gap-3 p-4">
                      <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
                        <ArrowRight className="text-red-600" size={20} />
                      </div>
                      <div>
                        <h3 className="text-sm font-semibold text-gray-900">Seitenschwenkbarer Auslegerarm</h3>
                      </div>
                    </CardContent>
                  </Card>

                  <Card className="flex-1 rounded-xl overflow-hidden bg-white shadow-[0_2px_8px_rgba(0,0,0,0.06)] hover:shadow-[0_4px_12px_rgba(0,0,0,0.1)] border border-gray-100 transition-all">
                    <CardContent className="h-full flex items-center gap-3 p-4">
                      <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
                        <Truck className="text-red-600" size={20} />
                      </div>
                      <div>
                        <h3 className="text-sm font-semibold text-gray-900">Spurverstellbares Fahrwerk</h3>
                      </div>
                    </CardContent>
                  </Card>

                  <Card className="flex-1 rounded-xl overflow-hidden bg-white shadow-[0_2px_8px_rgba(0,0,0,0.06)] hover:shadow-[0_4px_12px_rgba(0,0,0,0.1)] border border-gray-100 transition-all">
                    <CardContent className="h-full flex items-center gap-3 p-4">
                      <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
                        <Cog className="text-red-600" size={20} />
                      </div>
                      <div>
                        <h3 className="text-sm font-semibold text-gray-900">Manueller Schnellwechsler</h3>
                        <p className="text-xs text-gray-500">Werksseitig montiert</p>
                      </div>
                    </CardContent>
                  </Card>
                </div>

                {/* Mittlere Spalte: Video */}
                <div className="relative w-full overflow-hidden rounded-xl h-[400px] xl:h-[450px]">
                  <video
                    autoPlay
                    muted
                    loop
                    playsInline
                    preload="metadata"
                    poster="/videos/hero-poster.jpg"
                    className="w-full h-full object-cover object-center"
                    webkit-playsinline="true"
                  >
                    <source src="/videos/hero.mp4" type="video/mp4" />
                  </video>
                  
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent flex items-end">
                    <div className="px-6 pb-6 w-full text-center">
                      <h1 className="text-3xl xl:text-4xl font-bold text-white mb-2">Jannesmann Baumaschinen</h1>
                      <p className="text-sm xl:text-base text-gray-200">
                        Professionelle Baumaschinen — Serienausstattung inklusive
                      </p>
                    </div>
                  </div>
                </div>

                {/* Rechte Spalte: 4 Kacheln */}
                <div className="flex flex-col gap-3 h-[400px] xl:h-[450px]">
                  <Card className="flex-1 rounded-xl overflow-hidden bg-white shadow-[0_2px_8px_rgba(0,0,0,0.06)] hover:shadow-[0_4px_12px_rgba(0,0,0,0.1)] border border-gray-100 transition-all">
                    <CardContent className="h-full flex items-center gap-3 p-4">
                      <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
                        <Gauge className="text-red-600" size={20} />
                      </div>
                      <div>
                        <h3 className="text-sm font-semibold text-gray-900">Joystick-Steuerung</h3>
                      </div>
                    </CardContent>
                  </Card>

                  <Card className="flex-1 rounded-xl overflow-hidden bg-white shadow-[0_2px_8px_rgba(0,0,0,0.06)] hover:shadow-[0_4px_12px_rgba(0,0,0,0.1)] border border-gray-100 transition-all">
                    <CardContent className="h-full flex items-center gap-3 p-4">
                      <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
                        <Award className="text-red-600" size={20} />
                      </div>
                      <div>
                        <h3 className="text-sm font-semibold text-gray-900">Dieselmotor</h3>
                        <p className="text-xs text-gray-500">Mit Elektrostarter</p>
                      </div>
                    </CardContent>
                  </Card>

                  <Card className="flex-1 rounded-xl overflow-hidden bg-white shadow-[0_2px_8px_rgba(0,0,0,0.06)] hover:shadow-[0_4px_12px_rgba(0,0,0,0.1)] border border-gray-100 transition-all">
                    <CardContent className="h-full flex items-center gap-3 p-4">
                      <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
                        <Wrench className="text-red-600" size={20} />
                      </div>
                      <div>
                        <h3 className="text-sm font-semibold text-gray-900">Grabschaufel 30/40 cm</h3>
                        <p className="text-xs text-gray-500">Im Lieferumfang</p>
                      </div>
                    </CardContent>
                  </Card>

                  <Card className="flex-1 rounded-xl overflow-hidden bg-white shadow-[0_2px_8px_rgba(0,0,0,0.06)] hover:shadow-[0_4px_12px_rgba(0,0,0,0.1)] border border-gray-100 transition-all">
                    <CardContent className="h-full flex items-center gap-3 p-4">
                      <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
                        <CheckCircle className="text-red-600" size={20} />
                      </div>
                      <div>
                        <h3 className="text-sm font-semibold text-gray-900">Komplett montiert</h3>
                        <p className="text-xs text-gray-500">Betriebsbereit geliefert</p>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              </div>

              {/* Unterer Banner */}
              <div className="mt-5 xl:mt-6">
                <Card className="rounded-xl overflow-hidden bg-gradient-to-r from-red-600 to-red-700 shadow-lg border-0">
                  <CardContent className="py-6 xl:py-8">
                    <div className="flex items-center justify-center">
                      <div className="w-14 h-14 xl:w-16 xl:h-16 bg-white/20 rounded-full flex items-center justify-center mr-5">
                        <CheckCircle className="text-white" size={28} />
                      </div>
                      <div className="text-center">
                        <h3 className="text-2xl xl:text-3xl font-bold text-white mb-1">Serienausstattung inklusive</h3>
                        <p className="text-base xl:text-lg text-red-100">
                          Alle Maschinen werden <strong className="text-white">einsatzbereit montiert</strong> mit kompletter Serienausstattung geliefert
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>

            {/* Mobile/Tablet Layout: Video volle Breite */}
            <div className="lg:hidden">
              <div className="relative w-full overflow-hidden rounded-xl">
                <video
                  autoPlay
                  muted
                  loop
                  playsInline
                  preload="metadata"
                  poster="/videos/hero-poster.jpg"
                  className="w-full h-[280px] sm:h-[400px] md:h-[500px] object-cover object-center"
                  webkit-playsinline="true"
                >
                  <source src="/videos/hero.mp4" type="video/mp4" />
                </video>
                
                <div className="absolute inset-0 bg-gradient-to-r from-black/60 via-black/20 to-transparent flex items-end">
                  <div className="px-6 sm:px-12 pb-6 sm:pb-10">
                    <h1 className="text-2xl sm:text-4xl md:text-5xl font-bold text-white mb-2 sm:mb-4">Jannesmann Baumaschinen</h1>
                    <p className="text-sm sm:text-lg text-gray-200">
                      Professionelle Baumaschinen — Serienausstattung inklusive
                    </p>
                  </div>
                </div>
              </div>

              {/* Mobile Highlight-Kacheln */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-4">
                <Card className="rounded-xl bg-white shadow-[0_2px_8px_rgba(0,0,0,0.08)] border border-gray-100">
                  <CardContent className="pt-4 pb-4 text-center">
                    <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-2">
                      <Shield className="text-red-600" size={20} />
                    </div>
                    <h3 className="text-xs font-semibold text-gray-900 mb-1">Kabine & Steuerung</h3>
                    <p className="text-[10px] text-gray-500">Geschlossene Kabine &amp; Joystick</p>
                  </CardContent>
                </Card>
                <Card className="rounded-xl bg-white shadow-[0_2px_8px_rgba(0,0,0,0.08)] border border-gray-100">
                  <CardContent className="pt-4 pb-4 text-center">
                    <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-2">
                      <Cog className="text-red-600" size={20} />
                    </div>
                    <h3 className="text-xs font-semibold text-gray-900 mb-1">Hydraulik & Werkzeug</h3>
                    <p className="text-[10px] text-gray-500">Schnellwechsler &amp; Grabschaufel</p>
                  </CardContent>
                </Card>
                <Card className="rounded-xl bg-white shadow-[0_2px_8px_rgba(0,0,0,0.08)] border border-gray-100">
                  <CardContent className="pt-4 pb-4 text-center">
                    <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-2">
                      <Gauge className="text-red-600" size={20} />
                    </div>
                    <h3 className="text-xs font-semibold text-gray-900 mb-1">Fahrwerk & Ausleger</h3>
                    <p className="text-[10px] text-gray-500">Schwenkbar &amp; Spurverstellbar</p>
                  </CardContent>
                </Card>
                <Card className="rounded-xl bg-white shadow-[0_2px_8px_rgba(0,0,0,0.08)] border border-gray-100">
                  <CardContent className="pt-4 pb-4 text-center">
                    <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-2">
                      <Truck className="text-red-600" size={20} />
                    </div>
                    <h3 className="text-xs font-semibold text-gray-900 mb-1">Qualität & Sicherheit</h3>
                    <p className="text-[10px] text-gray-500">LED-Beleuchtung &amp; Einsatzbereit</p>
                  </CardContent>
                </Card>
              </div>

              {/* Roter Banner Mobile */}
              <div className="mt-4 bg-gradient-to-r from-red-600 to-red-700 rounded-xl p-5 text-center text-white shadow-xl">
                <div className="flex items-center justify-center mb-2">
                  <CheckCircle className="text-white mr-3" size={28} />
                  <h3 className="text-lg font-bold">Serienausstattung inklusive</h3>
                </div>
                <p className="text-sm text-red-100">
                  Alle Maschinen werden einsatzbereit montiert geliefert
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* PayPal-Ratenzahlung Banner (zwischen Hero & Bagger-Modellen) */}
      <section className="py-6 bg-gray-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-2 xl:max-w-[1800px]">
          <PayPalInstallmentBanner />
        </div>
      </section>

      {/* 2. Mini-Bagger Modelle */}
      <section id="bagger-modelle" className="py-8 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-2 xl:max-w-[1800px]">
          <div className="text-center mb-6">
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900">
              Bagger Modelle
            </h2>
          </div>

          {/* 4-Column Grid for Mini-Bagger Models */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {miniBagger.map((machine) => (
              <ProductCard
                key={machine.id}
                product={machine}
                linkPrefix="/bagger"
                showButtons={true}
              />
            ))}
          </div>
        </div>
      </section>

      {/* 3. Radlader - Ausgeblendet */}

      {/* 4. Gabelstapler */}
      <section className="py-4 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-2 xl:max-w-[1800px]">
          <div className="text-center mb-6">
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900">Gabelstapler</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {gabelstapler.map((machine) => (
              <ProductCard
                key={machine.id}
                product={machine}
                linkPrefix="/gabelstapler"
                showButtons={true}
              />
            ))}
          </div>
        </div>
      </section>

      {/* 5. Rüttelplatten */}
      <section className="py-4 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-2 xl:max-w-[1800px]">
          <div className="text-center mb-6">
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900">Rüttelplatten</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {ruettelplatten.map((machine) => (
              <ProductCard
                key={machine.id}
                product={machine}
                linkPrefix="/ruettelplatten"
                showButtons={true}
              />
            ))}
          </div>
        </div>
      </section>

      {/* 6. Stampfer */}
      <section className="py-4 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-2 xl:max-w-[1800px]">
          <div className="text-center mb-6">
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900">Stampfer</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {stampfer.map((machine) => (
              <ProductCard
                key={machine.id}
                product={machine}
                linkPrefix="/stampfer"
                showButtons={true}
              />
            ))}
          </div>
        </div>
      </section>

      {/* 7. Verfügbares Zubehör */}
      <section className="py-8 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-2 xl:max-w-[1800px]">
          <div className="text-center mb-6">
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900">Verfügbares Zubehör</h2>
          </div>

          {/* Zubehör-Kacheln mit Dual-Buttons */}
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
            {accessories.map((item) => (
              <ProductCard
                key={item.id}
                product={{
                  ...item,
                  pricing: {
                    buy: `${item.price?.toLocaleString('de-DE') || '0'} €`,
                    numericPrice: item.price || 0
                  }
                }}
                linkPrefix="/zubehoer"
                showButtons={true}
              />
            ))}
          </div>
        </div>
      </section>

      {/* 5. Info-Bereich: Features, Services, CTA - durchgehend grauer Hintergrund */}
      <section className="py-6 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-2 xl:max-w-[1800px]">
          <div className="bg-gray-50 rounded-xl p-4 sm:p-6 space-y-4 sm:space-y-6">
            
            {/* Features - Qualität, Service, Schnelle Verfügbarkeit, Persönliche Beratung */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
              {features.map((feature, index) => {
                const IconComponent = {
                  Award,
                  Wrench,
                  Clock,
                  HeadphonesIcon: Phone
                }[feature.icon];
                
                return (
                  <Card key={index} className="border-0 shadow-md hover:shadow-xl transition-shadow duration-300 bg-white">
                    <CardContent className="pt-6 pb-6">
                      <div className="flex flex-col items-center text-center">
                        <div className="w-12 h-12 sm:w-14 sm:h-14 bg-red-100 rounded-full flex items-center justify-center mb-3 sm:mb-4">
                          <IconComponent className="text-red-600" size={24} />
                        </div>
                        <h3 className="font-semibold text-sm sm:text-lg mb-2">{feature.title}</h3>
                        <p className="text-gray-600 text-xs sm:text-sm">{feature.description}</p>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>

            {/* Services - 3 Kacheln */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 md:gap-6">
              {services.map((service) => (
                <Card key={service.id} className="border-0 shadow-md hover:shadow-xl transition-shadow duration-300 bg-white">
                  <CardContent className="pt-6 pb-6">
                    <div className="flex flex-col items-center text-center">
                      <div className="w-12 h-12 sm:w-14 sm:h-14 bg-red-100 rounded-full flex items-center justify-center mb-3 sm:mb-4">
                        {service.id === 1 && <Award className="text-red-600" size={24} />}
                        {service.id === 2 && <Phone className="text-red-600" size={24} />}
                        {service.id === 3 && <Wrench className="text-red-600" size={24} />}
                      </div>
                      <h3 className="font-semibold text-sm sm:text-lg mb-2">{service.title}</h3>
                      <p className="text-gray-600 text-xs sm:text-sm mb-3">{service.description}</p>
                      <ul className="space-y-1 text-left w-full">
                        {service.features.slice(0, 3).map((feature, idx) => (
                          <li key={idx} className="flex items-start text-xs sm:text-sm">
                            <CheckCircle className="text-red-600 mr-2 flex-shrink-0 mt-0.5" size={14} />
                            <span className="text-gray-700">{feature}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            {/* CTA - Bereit für Ihr Projekt */}
            <Card className="border-0 shadow-md hover:shadow-xl transition-shadow duration-300 bg-white">
              <CardContent className="pt-8 pb-8">
                <div className="flex flex-col items-center text-center">
                  <div className="w-12 h-12 sm:w-14 sm:h-14 bg-red-100 rounded-full flex items-center justify-center mb-3 sm:mb-4">
                    <ArrowRight className="text-red-600" size={24} />
                  </div>
                  <h3 className="font-semibold text-lg sm:text-xl mb-2">{t('cta.readyForProject')}</h3>
                  <p className="text-gray-600 text-sm sm:text-base mb-4 max-w-xl">
                    {t('cta.contactUs')}
                  </p>
                  <button 
                    onClick={() => {
                      const element = document.getElementById('bagger-modelle');
                      const headerOffset = 150;
                      const elementPosition = element.getBoundingClientRect().top;
                      const offsetPosition = elementPosition + window.pageYOffset - headerOffset;
                      window.scrollTo({ top: offsetPosition, behavior: 'smooth' });
                    }}
                    className="bg-red-600 hover:bg-red-700 text-white px-6 py-3 rounded-lg font-medium transition-colors flex items-center"
                  >
                    {t('nav.bagger')}
                    <ArrowRight className="ml-2" size={20} />
                  </button>
                </div>
              </CardContent>
            </Card>

          </div>
        </div>
      </section>

      {/* 6. Zwei Videos am Ende */}
      <section className="py-6 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-2 xl:max-w-[1800px]">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 md:gap-6">
            {/* Video 2 */}
            <div className="relative rounded-xl overflow-hidden shadow-lg">
              <video
                autoPlay
                muted
                loop
                playsInline
                preload="metadata"
                poster="/videos/video2-poster.jpg"
                className="w-full h-[140px] sm:h-[220px] md:h-[400px] object-cover"
                webkit-playsinline="true"
              >
                <source src="/videos/video2.mp4" type="video/mp4" />
              </video>
            </div>

            {/* Video 3 */}
            <div className="relative rounded-xl overflow-hidden shadow-lg">
              <video
                autoPlay
                muted
                loop
                playsInline
                preload="metadata"
                poster="/videos/video3-poster.jpg"
                className="w-full h-[140px] sm:h-[220px] md:h-[400px] object-cover"
                webkit-playsinline="true"
              >
                <source src="/videos/video3.mp4" type="video/mp4" />
              </video>
            </div>
          </div>
        </div>
      </section>

      {/* End of main content */}
    </div>
  );
};

export default Home;
